import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getReviews } from '../redux/reviewSlice';
import Pagination from '../components/Pagination';
import ReviewDetailModal from '../components/ReviewDetailModal';
import { useSearchExport } from '../context/SearchExportContext';
import { EyeIcon } from '@heroicons/react/16/solid';
import { fetchReviews } from '../services/api'; // Assuming the API functions are in this file
import '../styles/spinner/spinner.css';
import ExportDropdown from '../context/ExportDropdown';
import RecordsLoader from '../components/RecordsLoader';
import ConfirmationDialog from '../components/ConfirmationDialog';

const ReviewList = () => {
    const dispatch = useDispatch();
    const { reviews, status } = useSelector((state) => state.reviews);
    const { filterData } = useSearchExport();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const reviewsPerPage = 50;
    const [loading, setLoading] = useState({}); // For button loaders

    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(() => () => { }); // Function to run on confirm

    useEffect(() => {
        dispatch(getReviews(null));  // Fetch all Reviews on component load
    }, [dispatch]);

    const currentreviews = filterData(reviews)
        .slice((currentPage - 1) * reviewsPerPage, currentPage * reviewsPerPage);

    const handleRowClick = (review) => {
        setSelectedReview(review);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedReview(null);
    };

    // Open the confirmation dialog with a specific message and action
    const openConfirmationDialog = (message, action) => {
        setConfirmationMessage(message);
        setConfirmAction(() => action);
        setIsConfirmationOpen(true);
    };

    // Handle close of the confirmation dialog
    const closeConfirmationDialog = () => {
        setIsConfirmationOpen(false);
    };

    // Handle toggle visibility with loader and confirmation dialog
    const handleToggleVisibility = (reviewId, currentStatus) => {
        openConfirmationDialog(
            `Are you sure you want to ${currentStatus ? 'hide' : 'show'} this review?`,
            async () => {
                // Confirm action
                setLoading((prev) => ({ ...prev, [reviewId]: true })); // Set loading state

                try {
                    await fetchReviews(reviewId);
                    dispatch(getReviews(null));  // Refresh reviews after toggle
                } catch (error) {
                    console.error('Error toggling visibility status:', error);
                } finally {
                    setLoading((prev) => ({ ...prev, [reviewId]: false })); // Remove loading state
                    closeConfirmationDialog(); // Close the dialog after completion
                }
            }
        );
    };

    return (
        <div className="container mx-auto p-6">

            {/* Render the loader if the status is 'idle' */}
            {status === 'idle' && <RecordsLoader text="Loading Reviews, please hold on..." />}

            <h1 className="text-2xl text-black font-bold mb-6">Reviews List</h1>

            {/* Export Buttons */}
            <ExportDropdown data={reviews} fileName="reviews" />

            {status === 'failed' && <p>Error loading Reviews</p>}

            {/* Open Call List Table */}
            {status === 'succeeded' && (
                <>
                    <table className="min-w-full table-auto bg-white shadow-md rounded-lg">
                        <thead>
                            <tr className="bg-gray-100 text-left">
                                <th className="px-4 py-2">ID</th>
                                <th className="px-4 py-2">User Name</th>
                                <th className="px-4 py-2">User Title</th>
                                <th className="px-4 py-2">User Type</th>
                                <th className="px-4 py-2">User Country</th>
                                <th className="px-4 py-2">Review</th>
                                <th className="px-4 py-2">Ratings</th>
                                <th className="px-4 py-2">View</th>
                                <th className="px-4 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentreviews.length > 0 ? (
                                currentreviews.map((review) => (
                                    <tr
                                        key={review.id}
                                        className="border-b cursor-pointer hover:bg-gray-100"
                                    >
                                        <td className="px-4 py-2">{review.id}</td>
                                        <td className="px-4 py-2">{review.name}</td>
                                        <td className="px-4 py-2">{review.title}</td>
                                        <td className="px-4 py-2">{review.user_type === "organization" ? "Organization" : "Artist"}</td>
                                        <td className="px-4 py-2">{review.country}</td>
                                        <td className="px-4 py-2">
                                            {review.review.split(' ').slice(0, 5).join(' ')}{review.review.split(' ').length > 5 ? '...' : ''}
                                        </td>
                                        <td className="px-4 py-2">{review.ratings}</td>
                                        <td className="px-4 py-2 flex">
                                            <EyeIcon className="size-6 text-blue-500 ml-4" onClick={() => handleRowClick(review)} />
                                        </td>
                                        <td className="px-4 py-2">
                                            <button
                                                onClick={() => handleToggleVisibility(review.id, review.visibility)}
                                                className={`w-24 h-10 flex items-center justify-center px-2 py-1 rounded ${review.visibility ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>
                                                {loading[review.id] ? (
                                                    <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-white border-t-transparent"></div>
                                                ) : (
                                                    review.visibility ? 'Not Visible' : 'Visible'
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center py-4">No Reviews Found!</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Open Call Detail Modal */}
                    <ReviewDetailModal
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        review={selectedReview}
                    />

                    {/* Confirmation Dialog */}
                    <ConfirmationDialog
                        isOpen={isConfirmationOpen}
                        message={confirmationMessage}
                        onConfirm={confirmAction}
                        onCancel={closeConfirmationDialog}
                    />

                    {/* Pagination */}
                    {reviews.length > reviewsPerPage && (
                        <Pagination
                            usersPerPage={reviewsPerPage}
                            totalUsers={reviews.length}
                            paginate={setCurrentPage}
                            currentPage={currentPage}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default ReviewList;